import {
  getInt32,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { AllowedDomain, AllowedDomainDTO } from "./allowedDomain";
/* eslint-disable import/export */
export abstract class GetAllowedDomainsRPC implements
  LeoRPC<GetAllowedDomainsRPC.Request, GetAllowedDomainsRPC.Response, GetAllowedDomainsRPC.Errors.Errors>
{
  abstract execute(
    request: GetAllowedDomainsRPC.Request
  ): Promise<LeoRPCResult<GetAllowedDomainsRPC.Response, GetAllowedDomainsRPC.Errors.Errors>>;
}

export namespace GetAllowedDomainsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    pageIndex: number;
    itemsPerPage: number;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetAllowedDomainsRPCRequest: boolean = true;
  
    readonly pageIndex: number;
    readonly itemsPerPage: number;
  
    static readonly constraints = {
      "pageIndex": {
        "minValue": 0,
      },
      "itemsPerPage": {
        "minValue": 10,
      }
    }
  
    constructor(
    pageIndex: number, 
    itemsPerPage: number
    ) {
      Request.validatePageIndex(pageIndex);
      this.pageIndex = pageIndex;
      Request.validateItemsPerPage(itemsPerPage);
      this.itemsPerPage = itemsPerPage;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const pageIndex = getInt32(dto, "pageIndex", InvalidRequestPageIndexError);
      const itemsPerPage = getInt32(dto, "itemsPerPage", InvalidRequestItemsPerPageError);
      return new Request(
        pageIndex,
        itemsPerPage
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          pageIndex: this.pageIndex,
          itemsPerPage: this.itemsPerPage
      };
      return dto;
    }
    private static validatePageIndex = (pageIndex: number): void => {
      if(pageIndex < 0) {
        throw new InvalidRequestPageIndexMinValueError(
          0,
          `Attribute pageIndex has value ${pageIndex}. Min value is 0.`
        );
      }
    }
    private static validateItemsPerPage = (itemsPerPage: number): void => {
      if(itemsPerPage < 10) {
        throw new InvalidRequestItemsPerPageMinValueError(
          10,
          `Attribute itemsPerPage has value ${itemsPerPage}. Min value is 10.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `GetAllowedDomainsRPC(pageIndex='${this.pageIndex}',itemsPerPage='${this.itemsPerPage}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    domains: AllowedDomainDTO[];
    totalItems: number;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetAllowedDomainsResponse: boolean = true;
  
    readonly domains: AllowedDomain[];
    readonly totalItems: number;
  
  
    constructor(
    domains: AllowedDomain[], 
    totalItems: number
    ) {
      this.domains = domains;
      this.totalItems = totalItems;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const domains = getList<AllowedDomainDTO, AllowedDomain>(
        dto,
        "domains",
        AllowedDomain.fromDTO,
        InvalidResponseDomainsError
      );
      const totalItems = getInt32(dto, "totalItems", InvalidResponseTotalItemsError);
      return new Response(
        domains,
        totalItems
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          domains: this.domains.map((e: AllowedDomain) => { return e.toDTO() }),
          totalItems: this.totalItems
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetAllowedDomains(domains='${this.domains}',totalItems='${this.totalItems}')`;
  };
  


  export namespace Errors {

    export type Errors = any;
    export type ErrorsDTO = any;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestPageIndexError extends GetAllowedDomainsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPageIndexError";
  }
}

export class InvalidRequestPageIndexMinValueError extends InvalidRequestPageIndexError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPageIndexMinValueError";
  }
}

export class InvalidRequestItemsPerPageError extends GetAllowedDomainsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestItemsPerPageError";
  }
}

export class InvalidRequestItemsPerPageMinValueError extends InvalidRequestItemsPerPageError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestItemsPerPageMinValueError";
  }
}

export class InvalidResponseDomainsError extends GetAllowedDomainsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDomainsError";
  }
}

export class InvalidResponseTotalItemsError extends GetAllowedDomainsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTotalItemsError";
  }
}

